import React from 'react';
import { WxbIcon } from '../Icon/Icon';
import './Collapse.css';

export interface WxbCollapseItem {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
}

export interface WxbCollapseProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: WxbCollapseItem[];
  activeKeys?: string[];
  defaultActiveKeys?: string[];
  accordion?: boolean;
  onChange?: (activeKeys: string[]) => void;
}

export const WxbCollapse: React.FC<WxbCollapseProps> = ({
  items,
  activeKeys: controlledActiveKeys,
  defaultActiveKeys = [],
  accordion = false,
  onChange,
  className = '',
  ...props
}) => {
  const [internalActiveKeys, setInternalActiveKeys] = React.useState<string[]>(defaultActiveKeys);
  const isControlled = controlledActiveKeys !== undefined;
  const activeKeys = isControlled ? controlledActiveKeys : internalActiveKeys;
  const componentId = React.useId().replace(/:/g, '');

  const toggle = (item: WxbCollapseItem) => {
    if (item.disabled) return;
    const nextActiveKeys = activeKeys.includes(item.key)
      ? activeKeys.filter((key) => key !== item.key)
      : accordion
        ? [item.key]
        : [...activeKeys, item.key];
    if (!isControlled) setInternalActiveKeys(nextActiveKeys);
    onChange?.(nextActiveKeys);
  };

  return (
    <div className={`wxb-collapse ${className}`} {...props}>
      {items.map((item, index) => {
        const open = activeKeys.includes(item.key);
        const headerId = `wxb-collapse-${componentId}-header-${index}`;
        const panelId = `wxb-collapse-${componentId}-panel-${index}`;

        return (
          <div
            key={item.key}
            className={`wxb-collapse-item ${open ? 'is-open' : ''} ${item.disabled ? 'is-disabled' : ''}`}
          >
            <button
              id={headerId}
              type="button"
              className="wxb-collapse-header"
              aria-expanded={open}
              aria-controls={panelId}
              disabled={item.disabled}
              onClick={() => toggle(item)}
            >
              <span className="wxb-collapse-arrow" aria-hidden="true">
                <WxbIcon name="chevron-right" size={16} />
              </span>
              <span className="wxb-collapse-title">{item.label}</span>
            </button>
            <div
              id={panelId}
              className="wxb-collapse-body"
              role="region"
              aria-labelledby={headerId}
              hidden={!open}
            >
              <div className="wxb-collapse-content">{item.children}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
